/**
 * Exit Intent Popup — Sitewide
 * ------------------------------
 * Renders on every page (via layout/theme.liquid). Fires when the visitor
 * shows intent to leave AND the cart currently has at least one item.
 *
 * Desktop: mouseleave at top of viewport
 * Mobile:  back button + app switch + scroll-up burst + idle timeout
 *
 * Lifecycle:
 *   1. Check localStorage cooldown / session — bail if recently shown
 *   2. Live /cart.js check — bail if cart is empty
 *   3. Wait for delay_seconds before arming triggers
 *   4. Listen for exit intent signals
 *   5. On trigger → re-check cart is non-empty → show modal, fire once
 *   6. On submit → form posts to hidden iframe, show success view
 */

(function () {
  'use strict';

  const SESSION_KEY = 'exitIntentFiredThisSession';

  const popup = document.getElementById('ExitIntentPopup');
  if (!popup) return;

  // =========================================
  // CONFIG
  // =========================================
  const config = {
    delaySeconds: parseInt(popup.dataset.delaySeconds, 10) || 3,
    discountCode: popup.dataset.discountCode || '',
    successMessage: popup.dataset.successMessage || '',
    mobileIdleSeconds: parseInt(popup.dataset.mobileIdleSeconds, 10) || 30,
  };

  // =========================================
  // SESSION FIRE-ONCE
  // Show the popup at most once per browser session. No multi-day cooldown —
  // once shown this session it won't show again until a new session.
  // =========================================
  function firedThisSession() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markFired() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) { /* storage blocked, no-op */ }
  }

  if (firedThisSession()) return;

  // =========================================
  // LIVE CART CHECK
  // Popup must only fire when the cart actually has items. Server-rendered
  // Liquid can be stale after AJAX add/remove, so query /cart.js live.
  // =========================================
  function fetchCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => (data && typeof data.item_count === 'number' ? data.item_count : null))
      .catch(() => null);
  }

  // =========================================
  // MODAL SHOW / HIDE
  // =========================================
  let lastFocusedElement = null;
  let showInProgress = false;
  // Last known cart item_count. Kept fresh at arm time and whenever the tab
  // becomes visible again, so showPopup() can open INSTANTLY off the cache
  // instead of blocking on a /cart.js round-trip. The live re-check still
  // runs in the background and retracts the popup if the cart emptied.
  let cachedCount = null;

  // Reveal the modal. No cart gate here — callers own the check.
  function openModal() {
    lastFocusedElement = document.activeElement;
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const firstInput = popup.querySelector('input, button');
    if (firstInput) firstInput.focus();

    document.dispatchEvent(new CustomEvent('exit-intent:shown'));
  }

  // Lock the popup in as "fired" once the cart is confirmed non-empty.
  function finalize() {
    markFired();
    detachTriggers();
  }

  function showPopup() {
    if (firedThisSession() || showInProgress || !popup.hidden) return;

    // Fast path: cache says the cart has items → open immediately, then
    // confirm live in the background. Only lock in ("fired") once confirmed;
    // if the cart emptied, retract and stay armed.
    if (cachedCount !== null && cachedCount >= 1) {
      openModal();
      showInProgress = true;
      fetchCartCount().then((count) => {
        if (count !== null) cachedCount = count;
        showInProgress = false;
        if (!count || count < 1) {
          hidePopup(); // cart emptied → retract, keep triggers armed
        } else {
          finalize();
        }
      });
      return;
    }

    // Cache unknown/empty → verify first so we never flash an empty-cart popup.
    showInProgress = true;
    fetchCartCount().then((count) => {
      if (count !== null) cachedCount = count;
      showInProgress = false;
      if (count && count >= 1) {
        openModal();
        finalize();
      }
    });
  }

  function hidePopup() {
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocusedElement) lastFocusedElement.focus();
  }

  function showSuccessView() {
    const captureView = popup.querySelector('[data-view="capture"]');
    const successView = popup.querySelector('[data-view="success"]');
    const successText = popup.querySelector('[data-success-text]');

    captureView.hidden = true;
    successView.hidden = false;

    if (successText) {
      successText.textContent = config.successMessage.replace(/\[code\]/g, config.discountCode);
    }

    successView.querySelector('button').focus();
    document.dispatchEvent(new CustomEvent('exit-intent:converted'));
  }

  // =========================================
  // DEVICE DETECTION
  // =========================================
  const isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  // =========================================
  // DESKTOP TRIGGER: mouse exits top of viewport
  // =========================================
  function onMouseLeave(e) {
    if (e.clientY <= 0) showPopup();
  }

  // =========================================
  // MOBILE TRIGGER 1: Back button
  // =========================================
  function onPopState() {
    showPopup();
    history.pushState({ exitIntentArmed: true }, '');
  }

  // =========================================
  // MOBILE TRIGGER 2: App switch / tab hidden
  // =========================================
  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      showPopup();
    } else {
      // Returned to the tab → refresh the cached cart count so the next
      // exit attempt opens instantly with an accurate gate.
      fetchCartCount().then((count) => {
        if (count !== null) cachedCount = count;
      });
    }
  }

  // =========================================
  // MOBILE TRIGGER 3: Rapid scroll-up burst
  // =========================================
  let lastScrollY = window.scrollY;
  let lastScrollTime = Date.now();

  function onScroll() {
    const now = Date.now();
    const scrollY = window.scrollY;
    const deltaY = lastScrollY - scrollY;
    const deltaTime = now - lastScrollTime;

    if (deltaY > 120 && deltaTime < 250 && scrollY < 300) {
      showPopup();
    }

    lastScrollY = scrollY;
    lastScrollTime = now;
  }

  // =========================================
  // MOBILE TRIGGER 4: Idle timeout
  // =========================================
  let idleTimer = null;
  const IDLE_TIMEOUT_MS = config.mobileIdleSeconds * 1000;

  function resetIdleTimer() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(showPopup, IDLE_TIMEOUT_MS);
  }

  function attachIdleTracker() {
    ['touchstart', 'touchmove', 'click', 'scroll'].forEach((evt) => {
      document.addEventListener(evt, resetIdleTimer, { passive: true });
    });
    resetIdleTimer();
  }

  function detachIdleTracker() {
    clearTimeout(idleTimer);
    ['touchstart', 'touchmove', 'click', 'scroll'].forEach((evt) => {
      document.removeEventListener(evt, resetIdleTimer);
    });
  }

  // =========================================
  // ATTACH / DETACH TRIGGERS (device-branched)
  // =========================================
  function attachTriggers() {
    if (isTouchDevice) {
      history.pushState({ exitIntentArmed: true }, '');
      window.addEventListener('popstate', onPopState);
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('scroll', onScroll, { passive: true });
      attachIdleTracker();
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function detachTriggers() {
    document.removeEventListener('mouseleave', onMouseLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('popstate', onPopState);
    detachIdleTracker();
  }

  // Arm triggers after delay — but only if the cart currently has items.
  fetchCartCount().then((count) => {
    if (count !== null) cachedCount = count;
    if (!count || count < 1) return; // empty cart → never arm anywhere
    setTimeout(attachTriggers, config.delaySeconds * 1000);
  });

  // =========================================
  // CLOSE HANDLERS
  // =========================================
  popup.querySelectorAll('[data-exit-intent-close]').forEach((el) => {
    el.addEventListener('click', hidePopup);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !popup.hidden) hidePopup();
  });

  // =========================================
  // COPY CODE BUTTON
  // =========================================
  const copyBtn = popup.querySelector('[data-copy-code]');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(config.discountCode);
        const original = copyBtn.textContent;
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = original; }, 2000);
      } catch (e) {
        const codeEl = popup.querySelector('[data-discount-display]');
        const range = document.createRange();
        range.selectNode(codeEl);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    });
  }

  // =========================================
  // FORM SUBMIT
  // Form posts natively to hidden iframe (target="ExitIntentSink"
  // in snippet). Show success view after brief delay.
  // =========================================
  const form = popup.querySelector('#ExitIntentForm');
  if (form) {
    form.addEventListener('submit', function (e) {
      // DON'T preventDefault — let native submit happen into the iframe

      const emailInput = form.querySelector('input[type="email"]');
      if (!emailInput || !emailInput.value || !emailInput.checkValidity()) {
        return;
      }

      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Generating...';

      // Optimistic UI: show success after brief delay
      setTimeout(function () {
        showSuccessView();
      }, 600);
    });
  }
})();